import { toast } from "sonner";
import QRCode from "qrcode";
import { fileUrl } from "./api";
import { letterheadHTML, officialSignatureHTML, withDept } from "../components/Letterhead";
import { labelsFor, svgFor } from "../components/VehicleDamageDiagram";

// Printed handover receipts stay Arabic (official document) regardless of UI language.
export const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Official new Saudi Riyal symbol (SAMA 2025) as an inline SVG string for printed HTML.
export const SAR_SVG = '<svg viewBox="0 0 1124.14 1256.39" fill="currentColor" aria-label="⃁" style="width:0.85em;height:0.85em;display:inline-block;vertical-align:-0.06em;margin:0 2px"><path d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z"/><path d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z"/></svg>';

// Faint fingerprint icon for the printed thumbprint box.
export const FP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"/><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2"/><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/><path d="M8.65 22c.21-.66.45-1.32.57-2"/><path d="M14 13.12c0 2.38 0 6.38-1 8.88"/><path d="M2 16h.01"/><path d="M21.8 16c.2-2 .131-5.354 0-6"/><path d="M9 6.8a6 6 0 0 1 9 5.2v2"/></svg>';

export const qrFor = async (assetId) => {
  try { return await QRCode.toDataURL(`${window.location.origin}/c/${assetId}`, { margin: 1, width: 150 }); }
  catch { return ""; }
};

const CUSTODY_ACK = "أقر أنا الموظف المذكور أعلاه باستلام العهدة الموضحة بياناتها بحالة سليمة، وأتعهد بالمحافظة عليها وإعادتها عند الطلب أو عند انتهاء الخدمة.";

// Builds the inspection proof block (checklist + damage diagram + photos) for the printed receipt.
const inspectionHTML = (insp, photos = []) => {
  if (!insp) return "";
  const checklist = insp.checklist || {};
  const LBL = labelsFor(insp.kind);
  const allKeys = Object.keys(LBL);
  const chkRows = `<div class="chkgrid">${allKeys.map((k) => `<div class="chk"><span class="${checklist[k] ? "ok" : "no"}">${checklist[k] ? "✓" : "✗"}</span> ${esc(LBL[k])}</div>`).join("")}</div>`;
  const damages = Array.isArray(insp.damages) ? insp.damages : [];
  const markers = damages.map((d, i) =>
    `<div class="mk" style="left:${d.x}%;top:${d.y}%">${i + 1}</div>`).join("");
  const dmgList = damages.length
    ? `<ol class="dmglist">${damages.map((d) => `<li>${esc(d.note || "ضرر")}</li>`).join("")}</ol>`
    : `<div class="muted">لا توجد أضرار مسجّلة</div>`;
  const photoImgs = (photos || []).filter((p) => p.file_id)
    .map((p) => `<img src="${fileUrl(p.file_id)}" />`).join("");
  return `
    <div class="insp">
      <h2>فحص حالة المركبة عند الاستلام</h2>
      ${insp.odometer != null && insp.odometer !== "" ? `<div class="odo">قراءة العدّاد: <b>${esc(String(insp.odometer))}</b> كم</div>` : ""}
      ${chkRows}
      <div class="dmgrow">
        <div class="diagram"><svg viewBox="0 0 200 320">${svgFor(insp.kind)}</svg>${markers}</div>
        <div class="dmgside"><div class="dmgttl">الأضرار المحددة</div>${dmgList}</div>
      </div>
      ${insp.note ? `<div class="inspnote"><b>ملاحظة الفحص:</b> ${esc(insp.note)}</div>` : ""}
      ${photoImgs ? `<div class="photottl">صور المركبة وقت الاستلام</div><div class="photos">${photoImgs}</div>` : ""}
    </div>`;
};

const INSP_CSS = `
  .insp{margin:18px 0;border:1px solid #e2e8f0;border-radius:10px;padding:14px}
  .insp h2{font-size:16px;margin:0 0 10px;color:#065f46}
  .insp .odo{font-size:13px;margin-bottom:8px;color:#334155}
  .chkgrid{display:grid;grid-template-columns:1fr 1fr;gap:4px 14px;font-size:12.5px;color:#334155}
  .chk .ok{color:#059669;font-weight:800} .chk .no{color:#e11d48;font-weight:800}
  .muted{color:#94a3b8;font-size:12px}
  .dmgrow{display:flex;gap:16px;align-items:flex-start;margin-top:12px}
  .diagram{position:relative;width:180px;flex:none} .diagram svg{width:180px;height:auto}
  .diagram .mk{position:absolute;transform:translate(-50%,-50%);width:20px;height:20px;border-radius:50%;background:#e11d48;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid #fff}
  .dmgside{flex:1} .dmgttl{font-weight:700;font-size:13px;color:#334155;margin-bottom:4px}
  .dmglist{margin:0;padding-inline-start:18px;font-size:12.5px;color:#334155;line-height:1.8}
  .inspnote{margin-top:10px;font-size:13px;color:#334155;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px}
  .photottl{font-weight:700;font-size:13px;color:#334155;margin:12px 0 6px}
  .photos{display:flex;flex-wrap:wrap;gap:8px} .photos img{width:120px;height:90px;object-fit:cover;border:1px solid #e2e8f0;border-radius:8px}
`;

// opts: { title, typeLabel, idLabel, ack } — defaults suit generic custody; vehicles pass overrides.
export const printReceipt = async (asset, asg, opts = {}) => {
  const title = opts.title || "سند استلام عُهدة";
  const typeLabel = opts.typeLabel || "نوع العهدة";
  const idLabel = opts.idLabel || "الرقم / المعرّف";
  const ack = opts.ack || CUSTODY_ACK;
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة للطباعة"); return; }
  const qr = await qrFor(asset.id);
  const sig = asg.signature_id
    ? `<div class="sigbox"><img src="${fileUrl(asg.signature_id)}" alt="توقيع"/><div class="cap">توقيع المستلم</div></div>`
    : `<div class="sigline"><div class="cap">توقيع المستلم</div><div class="line"></div></div>`;
  w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
      *{box-sizing:border-box} body{font-family:SaudiRiyal,'Tajawal','IBM Plex Sans Arabic',sans-serif;color:#0f172a;padding:40px;max-width:760px;margin:auto}
      .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #059669;padding-bottom:14px;margin-bottom:22px}
      .head h1{font-size:24px;margin:0} .org{color:#065f46;font-weight:700;font-size:15px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:14px}
      th,td{border:1px solid #e2e8f0;padding:10px 12px;text-align:right} th{background:#f1f5f9;width:130px;font-weight:700;color:#334155}
      .ack{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px;font-size:14px;line-height:1.9;color:#14532d}
      .sigrow{display:flex;justify-content:space-between;align-items:flex-end;margin-top:34px;gap:30px}
      .sigbox img{height:80px;object-fit:contain;border-bottom:1px solid #94a3b8;padding-bottom:4px}
      .sigline .line{width:220px;border-bottom:1px solid #94a3b8;height:60px} .cap{margin-top:6px;color:#64748b;font-size:13px}
      .lsig{display:flex;gap:22px;align-items:flex-end}
      .fpbox{text-align:center}
      .fpbox .fp{width:88px;height:88px;border:1px dashed #94a3b8;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#cbd5e1;background:#fafafa}
      .fpbox .fp svg{width:46px;height:46px}
      .qr{text-align:center} .qr img{width:120px;height:120px} .qr .cap{margin-top:4px;font-size:11px}
      .foot{margin-top:30px;color:#94a3b8;font-size:12px;text-align:center;border-top:1px dashed #cbd5e1;padding-top:12px}
      ${INSP_CSS}
      @media print{body{padding:20px}}
    </style>
    <script>window.onload=function(){setTimeout(function(){window.print()},400)}<\/script>
    </head><body>
    ${letterheadHTML(opts.company || {})}
    <div class="head"><h1>${esc(title)}</h1></div>
    <table>
      <tr><th>اسم الموظف</th><td>${esc(asg.employee_name)}</td><th>رقم الهوية</th><td>${esc(asg.iqama_number || "—")}</td></tr>
      <tr><th>الفرع</th><td>${esc(asg.branch || asset.branch || "—")}</td><th>${esc(typeLabel)}</th><td>${esc(asset.type)}</td></tr>
      <tr><th>${esc(idLabel)}</th><td colspan="3">${esc(asset.identifier || "—")}</td></tr>
      ${asset.fuel_code ? `<tr><th>كود البنزين</th><td colspan="3" style="font-weight:700;letter-spacing:1px">${esc(asset.fuel_code)}</td></tr>` : ""}
      ${asset.operating_card_number ? `<tr><th>كرت التشغيل</th><td>${esc(asset.operating_card_number)}</td><th>انتهاء كرت التشغيل</th><td>${esc(asset.operating_card_expiry || "—")}</td></tr>` : ""}
      ${(asset.registration_expiry || asset.inspection_expiry) ? `<tr><th>انتهاء استمارة السير</th><td>${esc(asset.registration_expiry || "—")}</td><th>انتهاء الفحص الدوري</th><td>${esc(asset.inspection_expiry || "—")}</td></tr>` : ""}
      ${asset.chassis_number ? `<tr><th>رقم الهيكل</th><td>${esc(asset.chassis_number)}</td><th>اللون</th><td>${esc(asset.color || "—")}</td></tr>` : ""}
      <tr><th>تاريخ الاستلام</th><td>${esc(asg.received_at || "—")}</td><th>تاريخ الإرجاع</th><td>${esc(asg.returned_at || "لا يزال بحوزته")}</td></tr>
      <tr><th>ملاحظات</th><td colspan="3">${esc(asg.note || "—")}</td></tr>
    </table>
    ${inspectionHTML(opts.inspection, opts.photos)}
    <div class="ack">${esc(ack)}</div>
    <div class="sigrow">
      <div class="lsig">
        ${sig}
        <div class="fpbox"><div class="fp">${FP_SVG}</div><div class="cap">بصمة المستلم</div></div>
      </div>
      ${qr ? `<div class="qr"><img src="${qr}"/><div class="cap">امسح لفتح السجل</div></div>` : ""}
      ${officialSignatureHTML(withDept(opts.company || {}, "ops"))}
    </div>
    <div class="foot">تاريخ الطباعة: ${new Date().toLocaleDateString("ar-EG")}</div>
    </body></html>`);
  w.document.close();
  w.focus();
};

const VEHICLE_ACK = "أقر أنا الموظف المذكور أعلاه باستلام المركبة الموضحة بياناتها بحالة سليمة، وأتعهد بالمحافظة عليها والالتزام بأنظمة المرور وإعادتها عند الطلب أو عند انتهاء الخدمة.";

export const printVehicleReceipt = (asset, asg, company) => printReceipt(asset, asg, {
  title: "سند تسليم مركبة", typeLabel: "نوع المركبة", idLabel: "رقم اللوحة", ack: VEHICLE_ACK, company,
  inspection: asg.inspection,
  photos: (asg.photos && asg.photos.length)
    ? asg.photos
    : ((asg.inspection && Array.isArray(asg.inspection.photo_ids)) ? asg.inspection.photo_ids.map((id) => ({ file_id: id })) : []),
});

// Cash salary receipt — signed acknowledgment that the employee received salary in cash.
export const printSalaryReceipt = async (receipt, company = {}) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة للطباعة"); return; }
  const amt = Number(receipt.amount || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
  const sigSrc = receipt.signature_dataurl || (receipt.signature_id ? fileUrl(receipt.signature_id) : "");
  const sig = sigSrc
    ? `<div class="sigbox"><img src="${sigSrc}" alt="توقيع"/><div class="cap">توقيع المستلم</div></div>`
    : `<div class="sigline"><div class="cap">توقيع المستلم</div><div class="line"></div></div>`;
  const ack = `أقر أنا الموظف / ${esc(receipt.employee_name)}${receipt.national_id ? ` (هوية رقم ${esc(receipt.national_id)})` : ""} باستلامي مبلغ راتبي نقدًا وقدره ${amt} ${SAR_SVG} عن شهر ${esc(receipt.month)}، وأتنازل عن أي مطالبة به لاحقًا.`;
  w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سند قبض راتب نقدي</title>
    <style>
      *{box-sizing:border-box} body{font-family:SaudiRiyal,'Tajawal','IBM Plex Sans Arabic',sans-serif;color:#0f172a;padding:40px;max-width:760px;margin:auto}
      .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #059669;padding-bottom:14px;margin-bottom:22px}
      .head h1{font-size:24px;margin:0}
      .rcpt{color:#065f46;font-weight:700;font-size:14px}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:14px}
      th,td{border:1px solid #e2e8f0;padding:10px 12px;text-align:right} th{background:#f1f5f9;width:130px;font-weight:700;color:#334155}
      .amount{font-size:20px;font-weight:800;color:#065f46;letter-spacing:.5px}
      .ack{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px;font-size:14px;line-height:1.9;color:#14532d}
      .sigrow{display:flex;justify-content:space-between;align-items:flex-end;margin-top:34px;gap:30px}
      .sigbox img{height:80px;object-fit:contain;border-bottom:1px solid #94a3b8;padding-bottom:4px}
      .sigline .line{width:220px;border-bottom:1px solid #94a3b8;height:60px} .cap{margin-top:6px;color:#64748b;font-size:13px}
      .lsig{display:flex;gap:26px;align-items:flex-end}
      .fpbox{text-align:center}
      .fpbox .fp{width:88px;height:88px;border:1px dashed #94a3b8;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#cbd5e1;background:#fafafa}
      .fpbox .fp svg{width:46px;height:46px}
      .foot{margin-top:30px;color:#94a3b8;font-size:12px;text-align:center;border-top:1px dashed #cbd5e1;padding-top:12px}
      @media print{body{padding:20px}}
    </style>
    <script>window.onload=function(){setTimeout(function(){window.print()},400)}<\/script>
    </head><body>
    ${letterheadHTML(company)}
    <div class="head"><h1>سند قبض راتب نقدي</h1><div class="rcpt">رقم السند: ${esc(receipt.receipt_no || "—")}</div></div>
    <table>
      <tr><th>اسم الموظف</th><td>${esc(receipt.employee_name)}</td><th>رقم الهوية</th><td dir="ltr">${esc(receipt.national_id || "—")}</td></tr>
      <tr><th>الشهر</th><td>${esc(receipt.month)}</td><th>المبلغ</th><td class="amount" dir="ltr">${amt} ${SAR_SVG}</td></tr>
      <tr><th>ملاحظات</th><td colspan="3">${esc(receipt.note || "—")}</td></tr>
    </table>
    <div class="ack">${ack}</div>
    <div class="sigrow">
      <div class="lsig">
        ${sig}
        <div class="fpbox"><div class="fp">${FP_SVG}</div><div class="cap">بصمة الموظف</div></div>
      </div>
      ${officialSignatureHTML(withDept(company, "ops"))}
    </div>
    <div class="foot">تاريخ الطباعة: ${new Date().toLocaleDateString("ar-EG")}</div>
    </body></html>`);
  w.document.close();
  w.focus();
};

export const printGroupReceipt = async (emp, company = {}) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة للطباعة"); return; }
  const qrs = await Promise.all(emp.items.map((it) => (it.id ? qrFor(it.id) : Promise.resolve(""))));
  const rows = emp.items.map((it, i) =>
    `<tr><td style="text-align:center">${i + 1}</td><td>${esc(it.type)}</td><td>${esc(it.identifier || "—")}</td><td>${esc(it.since || "")}</td><td style="text-align:center">${qrs[i] ? `<img src="${qrs[i]}" style="width:64px;height:64px"/>` : "—"}</td></tr>`).join("");
  const sigItem = emp.items.find((it) => it.signature_id);
  const sig = sigItem
    ? `<div class="sigbox"><img src="${fileUrl(sigItem.signature_id)}" alt="توقيع"/><div class="cap">توقيع المستلم</div></div>`
    : `<div class="sigline"><div class="cap">توقيع المستلم</div><div class="line"></div></div>`;
  w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سند استلام عُهد</title>
    <style>
      *{box-sizing:border-box} body{font-family:SaudiRiyal,'Tajawal','IBM Plex Sans Arabic',sans-serif;color:#0f172a;padding:40px;max-width:760px;margin:auto}
      .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #059669;padding-bottom:14px;margin-bottom:22px}
      .head h1{font-size:24px;margin:0} .org{color:#065f46;font-weight:700;font-size:15px}
      .meta{font-size:14px;margin-bottom:16px} .meta b{color:#334155}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;font-size:14px}
      th,td{border:1px solid #e2e8f0;padding:9px 12px;text-align:right} th{background:#f1f5f9;font-weight:700;color:#334155}
      .ack{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px;font-size:14px;line-height:1.9;color:#14532d}
      .sigrow{display:flex;justify-content:space-between;margin-top:34px;gap:30px}
      .sigbox img{height:80px;object-fit:contain;border-bottom:1px solid #94a3b8;padding-bottom:4px}
      .sigline .line{width:220px;border-bottom:1px solid #94a3b8;height:60px} .cap{margin-top:6px;color:#64748b;font-size:13px}
      .lsig{display:flex;gap:22px;align-items:flex-end}
      .fpbox{text-align:center}
      .fpbox .fp{width:88px;height:88px;border:1px dashed #94a3b8;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#cbd5e1;background:#fafafa}
      .fpbox .fp svg{width:46px;height:46px}
      .foot{margin-top:40px;color:#94a3b8;font-size:12px;text-align:center;border-top:1px dashed #cbd5e1;padding-top:12px}
      @media print{body{padding:20px}}
    </style>
    <script>window.onload=function(){setTimeout(function(){window.print()},450)}<\/script>
    </head><body>
    ${letterheadHTML(company)}
    <div class="head"><h1>سند استلام عُهد (مجموعة)</h1></div>
    <div class="meta">المستلم: <b>${esc(emp.name)}</b>${emp.iqama ? ` · رقم الهوية: <b>${esc(emp.iqama)}</b>` : ""}${emp.branch ? ` — الفرع: <b>${esc(emp.branch)}</b>` : ""} · عدد الأصناف: <b>${emp.items.length}</b></div>
    <table>
      <thead><tr><th style="width:40px;text-align:center">#</th><th>الصنف</th><th>الرقم / المعرّف</th><th>تاريخ الاستلام</th><th style="width:80px;text-align:center">رمز المسح</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="ack">أقر أنا الموظف المذكور أعلاه باستلام العُهد الموضحة في الجدول بحالة سليمة، وأتعهد بالمحافظة عليها وإعادتها عند الطلب أو عند انتهاء الخدمة.</div>
    <div class="sigrow"><div class="lsig">${sig}<div class="fpbox"><div class="fp">${FP_SVG}</div><div class="cap">بصمة المستلم</div></div></div>${officialSignatureHTML(withDept(company, "ops"))}</div>
    <div class="foot">تاريخ الطباعة: ${new Date().toLocaleDateString("ar-EG")}</div>
    </body></html>`);
  w.document.close();
  w.focus();
};
